import React, { KeyboardEvent, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ProgressIndicator } from '../ProgressIndicator';
import { Button, ButtonSize, ButtonType } from '../Button';

export interface Props {
  children?: ReactNode;
  continueTitle?: string;
  familyRegistration: {
    currentStep: number;
    totalSteps: number;
  };
  headerBackground?: boolean;
  isContinueDisabled?: boolean;
  hideContinue?: boolean;
  hideProgress?: boolean;
  infoCard?: ReactNode;
  inverted?: boolean;
  invertedTitle?: boolean;
  navigate: (index: number) => void;
  onContinue?: () => void;
  title?: ReactNode;
  tracker: {
    track: (str: string, obj: object) => void;
  };
}

const useEventListener = (eventName: string, handler: (e: KeyboardEvent) => void): void => {
  const savedHandler = useRef((e: KeyboardEvent) => { console.warn(e) });

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const eventListener =
        (el: Event): void => savedHandler.current ? savedHandler.current(el as unknown as KeyboardEvent) : undefined;

    window.addEventListener(eventName, eventListener);

    return (): void => {
      window.removeEventListener(eventName, eventListener);
    };
    }

    return (): void => {};
  }, [eventName]);
};

export const NuxContainer = ({
  children, headerBackground, infoCard,
  inverted, invertedTitle, title,
  hideContinue, isContinueDisabled, hideProgress,
    continueTitle, onContinue, navigate,
    tracker, familyRegistration,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !isContinueDisabled && !hideContinue) {
      onContinue?.();
    }
  });

  const invertedOuterClasses = '!bg-sapphire md:!bg-emerald-300';
  const invertedContainerClasses = '!bg-transparent md:!bg-sapphire';
  const invertedButtonClasses = '!bg-white !text-sapphire-700';
  const invertedTitleClasses = '!text-white';

  const handleBack = (): void => {
    const trackingProps = {
      elementId: 'back-button',
      elementName: 'back-button',
    };

    tracker?.track('clicked-button', trackingProps);
    navigate(-1);
  }

  return (
    <div className={`flex md:bg-emerald-300 w-full min-h-screen md:min-h-[calc(100dvh-20px)] pb-24 lg:pb-40 justify-center md:pt-10 z-20 ${inverted ? invertedOuterClasses : ''}`}>
      <div className={`flex flex-col justify-between w-full md:w-[65%] md:bg-white lg:w-1/2 rounded-3xl ${inverted ? invertedContainerClasses : ''}`}>
        {headerBackground && (
          <div className='absolute -z-50 flex flex-col h-[250px] md:h-[160px] md:h-[250px] w-full sm:z-0 md:w-[65%] lg:w-1/2 bg-sapphire rounded-b-3xl md:rounded-t-3xl' />
        )}
        <div className='mb-3'>
          <div className='pt-2 xs:pt-8 sm:pt-8 mx-4 md:mx-8 flex flex-row items-center'>
            {
              !hideProgress && (
                <>
                  <button className='w-[5%] mr-2 pt-1 z-50 cursor-pointer' onClick={handleBack} type='button' id='btnBack' aria-label='Previous screen'>
                    <FontAwesomeIcon icon={faAngleLeft} size='lg' className={`text-almostBlack ${(inverted || invertedTitle) ? invertedTitleClasses : ''}`} />
                  </button>
                  <div className='w-[95%] z-20'>
                    <ProgressIndicator selected={familyRegistration.currentStep} total={familyRegistration.totalSteps} type={'bar' as const} />
                  </div>
                </>
              )
            }
          </div>
          {title && (<div className='pt-4 xs:px-12 sm:px-12 flex flex-col items-center'>
            <div className={`mb-3 px-3 font-semibold text-almostBlack text-xl z-20 text-center md:text-2xl whitespace-pre-line ${(inverted || invertedTitle) ? invertedTitleClasses : ''}`}>
              {title}
            </div>
            <div className='border-[0.25px] border-emerald w-[80px] h-[1px] z-20 xs:mt-1 sm:mt-1' />
          </div>
          )}
        </div>
        <div className='flex flex-col grow justify-start items-center'>
          {children}
        </div>
        {infoCard && (<div className='my-2 md:mb-4 mx-auto sm:w-[90%] xl:w-[70%]'>
          {infoCard}
        </div>)}
        {!hideContinue && (<div className='flex flex-col flex-wrap content-center sm:mb-2 md:mb-10'>
          <Button
            buttonType={ButtonType.primary}
            buttonSize={ButtonSize.wide}
            onClick={onContinue}
            role='button'
            aria-label={continueTitle ?? t('common.next')}
            disabled={isContinueDisabled}
            className={`w-[50%] ${inverted ? invertedButtonClasses : ''}`}
          >
            {continueTitle ?? t('common.next')}
          </Button>
        </div>)}
      </div>
    </div>
  );
};
